import React from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import { optionKey } from '../phoneTreeKeys';
import type {
  AgentDrawerFormValues,
  EscalationRule,
  PhoneTree,
} from '../types';

export const RouteConditionSelect: React.FC<{
  form: FormInstance<AgentDrawerFormValues>;
  destination: string;
  options: { value: string; label: string }[];
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}> = ({ form, destination, options, value = [], onChange, disabled }) => {
  const saved: PhoneTree | undefined = Form.useWatch('phone_tree', form);
  const menu: PhoneTree = {
    enabled: saved?.enabled === true,
    options: saved?.options ?? [],
    skip_intro: saved?.skip_intro === true,
    repeat_key: saved?.repeat_key,
    greeting: saved?.greeting,
  };
  const rules: EscalationRule[] = Form.useWatch('escalation_rules', form) ?? [];
  const defaults: string[] = Form.useWatch('escalate_when', form) ?? [];
  const used = new Set([
    ...(destination === 'default' ? [] : defaults),
    ...rules.flatMap((rule) =>
      rule.id === destination ? [] : rule.escalate_when
    ),
  ]);
  const spoken = options.map((option) => ({
    ...option,
    disabled: used.has(option.value) && !value.includes(option.value),
  }));
  const keys = menu.options.map((option, index) => ({
    value: `key:${option.id}`,
    label: `${optionKey(option, index)} ${option.label || 'Untitled'}`,
    tagLabel: `Press ${optionKey(option, index)} · ${option.label || 'Untitled'}`,
    disabled: !!option.destination && option.destination !== destination,
  }));
  const selected = [
    ...value,
    ...menu.options
      .filter((option) => option.destination === destination)
      .map((option) => `key:${option.id}`),
  ];
  return (
    <CheckboxMultiSelect
      showTagCloseIcon={!disabled}
      background='#5E5E5E'
      color='#fff'
      className='agentSelect agentSelect--darkText'
      disabled={disabled}
      titleText={
        menu.enabled ? 'Route / Escalate when caller...' : 'Escalate when'
      }
      groups={
        menu.enabled
          ? [
              { label: 'Caller presses...', options: keys },
              { label: 'Caller asks or says...', options: spoken },
            ]
          : undefined
      }
      stagedGroups={menu.enabled}
      options={spoken}
      value={menu.enabled ? selected : value}
      onChange={(next) => {
        onChange?.(
          next.filter((item) => !String(item).startsWith('key:')).map(String)
        );
        if (menu.enabled)
          form.setFieldValue('phone_tree', {
            ...menu,
            options: menu.options.map((option) => ({
              ...option,
              destination: next.includes(`key:${option.id}`)
                ? destination
                : option.destination === destination
                  ? ''
                  : option.destination,
            })),
          });
      }}
    />
  );
};
